import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { socureUploadDocument } from '../../store/actions/documents/documentsActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import { PAGES } from '../../constants/pages';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import DropzoneArea from '../../components/DropzoneArea';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  DOCUMENT_TYPES,
  KYC_DOC_TYPE_FOR_UPLOAD,
  KYC_DOCUMENT_TYPES,
  KYC_PERSON_TARGET_TYPES,
} from '../../util/constants';
import {
  ACCOUNT_TYPE_GTM,
  CONTENT_TYPE,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import Paragraph from '../../components/Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { separateByUppercaseAndJoinedWithHorLine } from '../../util/helpers/stringHelpers';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

const DropZoneWrapperPassport = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KycUploadSocialSecurityDocuments = ({ applicantType, documentType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData();
  const history = useHistory();
  const selectedDocument = retrieveFromSessionStorage(KYC_SELECTED_OPTION);
  const [errors, setErrors] = useState('');
  const { BrokerName } = useSelector(selectSettings);
  const [uploadedDocuments, setUploadedDocuments] = useState({
    Document: '',
  });
  const getTitleTag = () => {
    switch (documentType) {
      case DOCUMENT_TYPES.SOCIAL_SECURITY_CARD:
        return t('kycVerification.titleTagproofOfSocialSecurity', {
          brokerName: BrokerName,
        });

      default:
        break;
    }
  };

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    storeInSessionStorage(CONTENT_TYPE, 'multipart/form-data');
  }, []);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc documents upload',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc documents upload',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onSubmit = () => {
    const formData = new FormData();
    formData.append('Document', uploadedDocuments.Document);
    formData.append(
      'Caption',
      KYC_DOCUMENT_TYPES[`${selectedDocument.DocumentType}`],
    );

    const onSuccess = () => {
      removeFromSessionStorage(CONTENT_TYPE);
      handleGtmEvent();
      history.push(PAGES.KYC_VERIFICATION);
    };
    const params = {
      personTargetType:
        KYC_PERSON_TARGET_TYPES[selectedDocument?.PersonTargetType],
      documentType:
        KYC_DOC_TYPE_FOR_UPLOAD[`${selectedDocument?.DocumentType}`],
    };

    dispatch(
      socureUploadDocument({
        ApplicationUid,
        params,
        data: formData,
        onSuccess,
        setErrors,
      }),
    );
  };

  const getPreviousPath = () =>
    PAGES.KYC_PROOF_OF_SOCIAL_SECURITY[String(applicantType).toUpperCase()]
      .UPLOAD[String(separateByUppercaseAndJoinedWithHorLine(documentType))];

  return (
    <BaseLayout previousPath={getPreviousPath()} isKycProcess="true" noLogoBox>
      <Helmet>
        <title>{getTitleTag()}</title>
      </Helmet>
      <Header
        titleMb={32}
        title={t('kycVerification.proofOfSocialSecurityTitle')}
      />
      <DropZoneWrapperPassport>
        <DropzoneArea
          onUpload={(file) =>
            setUploadedDocuments({
              ...uploadedDocuments,
              Document: file,
            })
          }
        />
      </DropZoneWrapperPassport>
      <Paragraph color={themeColors.colorErrorMessage} variant="normal">
        {errors}
      </Paragraph>
      <OnBoardingCta
        disabled={!uploadedDocuments.Document}
        onClick={onSubmit}
      />
    </BaseLayout>
  );
};

KycUploadSocialSecurityDocuments.propTypes = {
  applicantType: PropTypes.string,
  documentType: PropTypes.string,
};

export default KycUploadSocialSecurityDocuments;
